import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";
import { seo } from "@/lib/seo";

const POSTS: Array<{ slug: string; title: string; excerpt: string; date: string; image?: string }> =
  [
    {
      slug: "matcha-grades",
      title: "The 4 Universal Commercial Grades of Matcha",
      excerpt:
        "While no standardized international government grading body exists, the global tea industry adheres strictly to a de facto four-tier hierarchy determined by harvest timing and milling.",
      date: "June 2026",
      image: "/assets/images/journal/grades.png",
    },
    {
      slug: "science-of-matcha",
      title: "Masterclass: The Science & Standards of Matcha",
      excerpt:
        "Matcha is strictly defined as powdered green tea made by grinding dried Tencha leaves. Delve into the complex refining process and physical properties of Japan's most fragile tea.",
      date: "May 2026",
      image: "/assets/images/journal/matcha.png",
    },
    {
      slug: "regional-terroir",
      title: "Regional Terroir: Characteristics by Prefecture",
      excerpt:
        "Japan's diverse geographic climate creates distinct regional tea identities. From Shizuoka to Kagoshima, discover how terroir influences flavor.",
      date: "April 2026",
      image: "/assets/images/journal/terroir.png",
    },
    {
      slug: "biochemical-composition",
      title: "Biochemical Composition & Health Benefits",
      excerpt:
        "Japanese green tea is a powerhouse of bio-active functional compounds. Discover the science behind catechins, L-Theanine, and essential vitamins.",
      date: "March 2026",
      image: "/assets/images/journal/health.png",
    },
    {
      slug: "traditional-tea-typologies",
      title: "The 12 Traditional Japanese Tea Typologies",
      excerpt:
        "From deeply shaded Gyokuro to roasted Hojicha, explore the defining processing styles and sensory characteristics of Japan's most essential teas.",
      date: "February 2026",
      image: "/assets/images/journal/typologies.png",
    },
    {
      slug: "green-tea-production",
      title: "The Step-by-Step Green Tea Production Process",
      excerpt:
        "Crafting premium Japanese green tea involves an unbroken sequence of specialized steps designed to preserve vibrant color, complex aromatics, and structural umami.",
      date: "January 2026",
      image: "/assets/images/journal/production.png",
    },
    {
      slug: "what-is-tea",
      title: "Botanical Foundations: What is Tea?",
      excerpt:
        "All authentic tea originates from the leaves of a single botanical species: Camellia sinensis. Discover how enzymatic fermentation defines global tea styles.",
      date: "December 2025",
      image: "/assets/images/journal/botanical.png",
    },

    {
      slug: "private-label-packaging-basics",
      title: "Private-label packaging: what to think about first",
      excerpt:
        "Tins, pouches, labels and lead times. A practical overview for brands launching a tea line.",
      date: "April 2026",
      image: "/assets/images/journal/packaging.png",
    },
    {
      slug: "hojicha-the-quiet-staple",
      title: "Hojicha — the quiet staple",
      excerpt: "Why roasted green tea has become a wholesale favourite for cafés and hotels.",
      date: "March 2026",
      image: "/assets/images/journal/hojicha.png",
    },
  ];

export const Route = createFileRoute("/journal/")({
  head: () => ({
    meta: [
      ...seo({
        title: "Journal — QT Foods Wholesale",
        description: "Notes on Japanese tea, sourcing and wholesale from QT Foods.",
      }),
    ],
  }),
  component: Page,
  loader: () => POSTS,
});

function Page() {
  return (
    <SiteLayout breadcrumbs={[{ label: "Journal" }]}>
      <section className="mx-auto max-w-5xl px-5 pt-10 pb-20 lg:px-8 lg:pt-14 lg:pb-24">
        <h1 className="mt-3 font-serif text-5xl md:text-6xl">Notes on tea & trade.</h1>
        <div className="mt-10 grid grid-cols-1 items-start gap-12 lg:mt-14 lg:grid-cols-[1fr_280px]">
          <div className="order-2 lg:order-1">
            <ul className="divide-y divide-border border-y border-border">
              {POSTS.map((p) => (
                <li key={p.slug} id={p.slug} className="py-8 scroll-mt-24">
                  <Link
                    to="/journal/$slug"
                    params={{ slug: p.slug }}
                    className="group grid gap-8 md:grid-cols-[1fr_240px] items-start"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {p.date}
                      </p>
                      <h2 className="mt-2 font-serif text-3xl group-hover:text-primary">
                        {p.title}
                      </h2>
                      <p className="mt-3 text-muted-foreground">{p.excerpt}</p>
                      <span className="mt-4 inline-block text-sm text-primary">Read →</span>
                    </div>
                    {p.image && (
                      <div className="hidden md:block overflow-hidden rounded-sm">
                        <img
                          src={p.image}
                          alt={p.title}
                          className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <aside className="order-1 lg:sticky lg:top-24 lg:order-2 rounded-sm bg-muted/30 p-6">
            <h2 className="mb-4 text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Quick Jump
            </h2>
            <ul className="flex flex-col gap-2">
              {POSTS.map((p) => (
                <li key={`jump-${p.slug}`}>
                  <a
                    href={`#${p.slug}`}
                    className="text-sm text-foreground underline decoration-border underline-offset-4 transition-colors hover:text-primary hover:decoration-primary"
                  >
                    {p.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}

export { POSTS };
